'use client'

import { Eye, Home, TrendingUp } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import './index.scss'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Line,
} from 'recharts'
import { useTranslation } from '@payloadcms/ui'
import Image from 'next/image'


export const DashboardStats: React.FC = () => {
  const { i18n, t } = useTranslation()
  const [stats, setStats] = useState<{
    totalProperties: number
    totalViews: number
    chartData: { type: string; count: number }[]
    topViewed: { id: string; title: string; views: number; thumbnail?: string; isVideo?: boolean }[]
    viewsPerDate: { date: string; count: number }[]
  } | null>(null)

  useEffect(() => {
    const fetchStats = async () => {
      const res = await fetch('/api/agency/dashboard-stats')
      const data = await res.json()

      setStats(data)
    }
    fetchStats()
  }, [])

  const handleRedirect = (propertyId: string) => () => {
    if (!propertyId) return
    window.open(`/admin/collections/properties/${propertyId}`, '_blank')
  }

  const getColorByType = (type: string) => {
    switch (type.toLowerCase()) {
      case 'apartment':
        return '#22C55E'
      case 'house':
        return '#ff6e54'
      case 'land':
        return '#84CC16'
      case 'office':
        return '#3B82F6'
      case 'villa':
        return '#F59E0B'
      default:
        return '#003f5c'
    }
  }

  const showLabelByType = (type: string, percent: number) => {
    return `${(percent * 100).toFixed(0)}%`;
  }

  if (!stats) {
    return (
      <div className="dashboard-skeleton">
        <section className="dashboard-stats">
          <div className="stat-card skeleton"></div>
          <div className="stat-card skeleton"></div>
        </section>

        <div className="charts-container">
          <section className="chart-section">
            <div className="line-chart-skeleton"></div>
          </section>

          <section className="chart-section">
            <div className="chart-skeleton"></div>
          </section>
        </div>

        <section className="top-viewed-section">
          <div className="skeleton-title"></div>
          <div className="top-viewed-list">
            {[1, 2, 3].map((i) => (
              <div key={i} className="top-viewed-card skeleton-card">
                <div className="thumb-skeleton"></div>
                <div className="text-skeleton-group">
                  <div className="line-short"></div>
                  <div className="line-long"></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    )
  }

  return (
    <div>
      <section className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-info">
            <p className="stat-title">
              {t('custom:totalProperties') ?? 'Total Properties'}
            </p>
            <h2 className="stat-value">{stats?.totalProperties}</h2>
          </div>
          <div className="stat-icon">
            <Home size={26} />
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-info">
            <p className="stat-title">
              {t('custom:totalViews') ?? 'Total Views'}
            </p>
            <h2 className="stat-value">{stats?.totalViews}</h2>
          </div>
          <div className="stat-icon">
            <TrendingUp size={26} />
          </div>
        </div>
      </section>

      <div className="charts-container">
        {/* ---- Views Over Time ---- */}
        <section className="chart-section">
          <h3 className="chart-title">
            {t('custom:viewsOverTime') ?? 'Views Over Time'}
          </h3>
          {stats?.viewsPerDate?.length === 0 && (
            <p className="empty-data">
              {t('custom:noData') ?? 'No data available at the moment.'}
            </p>
          )}
          {stats?.viewsPerDate?.length > 0 && (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats.viewsPerDate}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis width="auto" />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#1eaf4e"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          </section>

          {/* Donut Chart */}
          <section className="chart-section">
            <h3 className="chart-title">
              {t('custom:propertiesByType') ?? 'Properties by Type'}
            </h3>
            {stats?.chartData?.length === 0 && (
              <p className="empty-data">{t('custom:noData') ?? 'No data available at the moment.'}</p>
            )}
            {stats?.chartData?.length > 0 && (
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stats?.chartData}
                      dataKey="count"
                      nameKey="type"
                      // innerRadius={70}
                      // outerRadius={100}
                      // paddingAngle={5}
                      label={({ type, percent }) => showLabelByType(type, percent)}
                    >
                      {stats?.chartData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getColorByType(entry.type)} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => [`${v} ${t('custom:properties') ?? 'Properties'}`, t('custom:count') ?? 'Count']} />
                    <Legend formatter={(v: string)=> t(`types:${v}`)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>
      </div>

      {/* Top 3 Properties */}
      <section className="top-viewed-section">
        <h3 className="top-viewed-title">
          {t('custom:topViewedProperties') ?? 'Top 3 Most Viewed Properties'}
        </h3>
        <div className="top-viewed-list">
          {stats?.topViewed?.length === 0 && (
            <p className="empty-data">
              {t('custom:noData') ?? 'No data available at the moment.'}
            </p>
          )}
          {stats?.topViewed?.length > 0 &&
            stats?.topViewed?.map((property, index) => (
              <div className="top-viewed-card" key={property.id} onClick={handleRedirect(property.id)}>
                {property.thumbnail ? (
                  <div className="top-viewed-thumb-wrapper">
                    <Image
                      src={property.thumbnail}
                      alt={property.title}
                      className="top-viewed-thumb"
                      width={100}
                      height={100}
                    />
                  </div>
                ) : (
                  <div className="top-viewed-thumb placeholder">
                    <Home size={20} />
                  </div>
                )}
                <div className="top-viewed-info">
                  <p className="top-viewed-title">
                    {index + 1}. {property.title}
                  </p>
                  <div className="top-viewed-views">
                    <Eye size={16} />
                    <span>{property.views} {t('custom:views') ?? 'Views'}</span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </section>
    </div>
  )
}
